import { AppLayout, State, useLayout } from "core";
import {
  FocusableSection,
  Config,
  useSpatialNavigation,
} from "@blacknut/spatialnav-sdk/dist";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom-v5-compat";
import { useOrientation } from "../../utils/OrientationContext";
import NavsLinks from "./NavLinks/NavsLinks";
import NavsLinksMobile from "./NavsLinksMobile/NavsLinksMobile";
import React from "react";
import { useProfiler } from "../../hooks/useProfiler";
const LEAVE_FOR = { down: "" };
const CONFIG: Config = { distanceMode: true, restrict: "self-first" };
const getActiveItemFromLocation = (pathname: string) => {
  if (pathname.match(/account/)) {
    return "account";
  }
  switch (pathname) {
    case "/browse":
      return "browse";
    case "/myList":
      return "myList";
    case "/search":
      return "search";
    case "/catalog":
      return "catalog";
    case "/lemag":
      return "lemag";
    case "/plus":
      return "plus";
  }
  return undefined;
};

export interface MenuProps {
  className?: string;
  hidden: boolean;
}

const isActiveElementChildOfMain = () => {
  let parent: Element | null | undefined = document.activeElement;
  let isChildOfMain = false;
  do {
    isChildOfMain = parent?.id === "main";
    parent = parent?.parentElement;
  } while (parent && !isChildOfMain);
  return isChildOfMain;
};

const hasExpandedMenuFeature = (layout: AppLayout) => {
  return layout === AppLayout.DESKTOP || layout === AppLayout.TV;
};
// FIXME merge nav links & nav links mobile
const Menu = ({ className, hidden }: MenuProps) => {
  const { pathname } = useLocation();
  const profile = useSelector((state: State) => state.profilesState.profile);
  const { config } = useSelector((state: State) => state.globalState);
  const lemag = !!config?.features?.lemag;
  const layout = useLayout();
  const [activeItem, setActiveItem] = useState(
    getActiveItemFromLocation(pathname) || "catalog",
  );
  useEffect(() => {
    const active = getActiveItemFromLocation(pathname);
    if (active && active !== activeItem) {
      setActiveItem(active);
    }
  }, [activeItem, pathname]);

  const { orientation } = useOrientation();

  const navigableFilter = useCallback(
    (e: Element, sectionId: string, direction: "up" | "down" | "left" | "right") => {
      if (orientation === "LANDSCAPE") {
        // from catalog ?
        if (
          document.activeElement &&
          (direction === "up" || direction === "down") &&
          isActiveElementChildOfMain()
        ) {
          return false;
        }
        return true;
      }
      return (
        document.documentElement.scrollHeight - document.documentElement.scrollTop ===
        window.innerHeight
      );
    },
    [orientation],
  );
  const isSmartphone = layout === AppLayout.PHONE;

  const Root = useProfiler("menu");
  const blacknutPlus = !!config?.features?.blacknutplus;
  const blacknutBonus = !!config?.features?.blacknutbonus;
  const hasExpandedMenuFeat = hasExpandedMenuFeature(layout);
  const [menuExpanded, _setMenuExpanded] = useState(!hasExpandedMenuFeat);
  const setMenuExpanded = useCallback(() => {
    if (hasExpandedMenuFeat) {
      _setMenuExpanded(true);
    }
  }, [hasExpandedMenuFeat]);
  const setMenuCollapsed = useCallback(() => {
    if (hasExpandedMenuFeat) {
      _setMenuExpanded(false);
    }
  }, [hasExpandedMenuFeat]);
  const { active } = useSpatialNavigation();
  return (
    <Root>
      <FocusableSection
        focusKey="menu"
        navigableFilter={navigableFilter}
        leaveFor={LEAVE_FOR}
        disabled={hidden}
        config={CONFIG}
        onMouseEnter={setMenuExpanded}
        onMouseLeave={setMenuCollapsed}
        onFocus={active ? setMenuExpanded : undefined}
        onBlur={active ? setMenuCollapsed : undefined}
      >
        {isSmartphone && (
          <NavsLinksMobile
            layout={layout}
            pathname={pathname}
            activeItem={activeItem}
            lemag={lemag}
            profile={profile}
            blacknutPlus={blacknutPlus || blacknutBonus}
            className={className}
          />
        )}
        {!isSmartphone && (
          <NavsLinks
            layout={layout}
            pathname={pathname}
            activeItem={activeItem}
            lemag={lemag}
            profile={profile}
            blacknutPlus={blacknutPlus || blacknutBonus}
            className={className}
            expanded={menuExpanded}
          />
        )}
      </FocusableSection>
    </Root>
  );
};

export default React.memo(Menu);
